import { useRef } from 'react';
import type { AiReportResult } from '@order-pilot/shared';
import { Alert } from '@/components/Alert';
import { PageHeader } from '@/components/PageHeader';
import { Skeleton } from '@/components/States';
import { AskPilot } from '@/features/ask-pilot/AskPilot';
import type { AskPilotHandle } from '@/features/ask-pilot/AskPilot';
import { useAiCredits } from '@/features/ask-pilot/api';
import { CreditsCard, creditsLine } from '@/features/ask-pilot/CreditsCard';
import { SavedPanel } from '@/features/ask-pilot/SavedPanel';
import { getErrorMessage } from '@/lib/api';

/**
 * Ask Pilot: ask in plain English, get a chart and a table. Claude only picks
 * from a fixed list of figures; the numbers are this tenant's own, read the
 * way every other report reads them.
 */
export function AskPilotPage() {
  const credits = useAiCredits();
  const thread = useRef<AskPilotHandle>(null);
  const c = credits.data;
  const showSaved = (title: string, result: AiReportResult) => thread.current?.showSaved(title, result);

  return (
    <>
      <PageHeader
        title="Ask Pilot"
        description="Ask a question about your business in plain English."
        meta={c ? <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs tabular-nums text-slate-600">{creditsLine(c)}</span> : null}
      />
      {credits.isLoading ? (
        <div className="mx-auto max-w-3xl space-y-3">
          <Skeleton className="h-8 w-80" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : credits.isError || !c ? (
        <Alert tone="error">{getErrorMessage(credits.error, 'Could not load Ask Pilot.')}</Alert>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="min-w-0">
            <AskPilot ref={thread} credits={c} />
          </div>
          <aside className="flex flex-col gap-5 lg:sticky lg:top-4 lg:self-start">
            <CreditsCard credits={c} />
            <SavedPanel onOpen={showSaved} />
          </aside>
        </div>
      )}
    </>
  );
}
